
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Facebook, Instagram, Twitter, Linkedin, Mail, Phone, MapPin, Heart, ExternalLink } from "lucide-react";
import RecipeSuggestionsFloatingButton from "./RecipeSuggestionsFloatingButton";

const Footer = () => {
  const location = useLocation();
  const currentYear = new Date().getFullYear();

  // Don't show recipe button on the recipe suggestions page
  const showRecipeButton = location.pathname !== "/recipes";

  // Only show the detailed footer on landing pages
  const isLanding = location.pathname === "/" || location.pathname === "/pricing";

  if (!isLanding) {
    return (
      <footer className="border-t bg-white py-6 relative">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-600 text-sm">
            © {currentYear} ExpiTrack. All rights reserved.
          </p>
        </div>

        {showRecipeButton && <RecipeSuggestionsFloatingButton />}
      </footer>
    );
  }

  return (
    <footer className="bg-gray-50 pt-16 pb-8 relative">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Company Info */}
          <div>
            <h3 className="font-bold text-lg mb-4">ExpiTrack</h3>
            <p className="text-gray-600 mb-4">
              Intelligent expiry management with AI-powered expiration tracking to reduce waste and save money.
            </p>
            <div className="flex space-x-4 mt-4">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-expitrack-primary transition-colors">
                <Facebook size={18} />
                <span className="sr-only">Facebook</span>
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-expitrack-primary transition-colors">
                <Twitter size={18} />
                <span className="sr-only">Twitter</span>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-expitrack-primary transition-colors">
                <Instagram size={18} />
                <span className="sr-only">Instagram</span>
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-expitrack-primary transition-colors">
                <Linkedin size={18} />
                <span className="sr-only">LinkedIn</span>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-lg mb-4">Product</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/dashboard" className="text-gray-600 hover:text-expitrack-primary transition-colors">Dashboard</Link>
              </li>
              <li>
                <Link to="/inventory" className="text-gray-600 hover:text-expitrack-primary transition-colors">Inventory Management</Link>
              </li>
              <li>
                <Link to="/luna-chat" className="text-gray-600 hover:text-expitrack-primary transition-colors">Luna AI Assistant</Link>
              </li>
              <li>
                <Link to="/recipes" className="text-gray-600 hover:text-expitrack-primary transition-colors">Recipe Suggestions</Link>
              </li>
              <li>
                <Link to="/pricing" className="text-gray-600 hover:text-expitrack-primary transition-colors">Pricing</Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-bold text-lg mb-4">Company</h3>
            <ul className="space-y-3">
              <li>
                <a href="#" className="text-gray-600 hover:text-expitrack-primary transition-colors">About Us</a>
              </li>
              <li>
                <a href="#" className="text-gray-600 hover:text-expitrack-primary transition-colors">Careers</a>
              </li>
              <li>
                <a href="#" className="text-gray-600 hover:text-expitrack-primary transition-colors">Blog</a>
              </li>
              <li>
                <a href="#" className="text-gray-600 hover:text-expitrack-primary transition-colors">Privacy Policy</a>
              </li>
              <li>
                <a href="#" className="text-gray-600 hover:text-expitrack-primary transition-colors">Terms of Service</a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-bold text-lg mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <MapPin size={18} className="mr-2 text-expitrack-primary flex-shrink-0 mt-1" />
                <span className="text-gray-600">BMSIT<br />Yelahanka, Banglore
                India</span>
              </li>
              <li className="flex items-center">
                <Phone size={18} className="mr-2 text-expitrack-primary flex-shrink-0" />
                <a href="tel:+11234567890" className="text-gray-600 hover:text-expitrack-primary transition-colors">+91 7259763338</a>
              </li>
              <li className="flex items-center">
                <Mail size={18} className="mr-2 text-expitrack-primary flex-shrink-0" />
                <a href="mailto:info@expitrack.com" className="text-gray-600 hover:text-expitrack-primary transition-colors">expitrack@gmail.com</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Newsletter Subscription */}
        <div className="border-t border-gray-200 pt-8 pb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-bold text-lg mb-2">Stay Connected</h3>
              <p className="text-gray-600">Subscribe to our newsletter for the latest updates and tips.</p>
            </div>
            <div>
              <form className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  placeholder="Your email address"
                  className="flex-grow px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-expitrack-primary/20 focus:border-expitrack-primary"
                  required
                />
                <button
                  type="submit"
                  className="bg-expitrack-primary text-white px-4 py-2 rounded-md hover:bg-expitrack-primary/90 transition-colors whitespace-nowrap"
                >
                  Subscribe
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-200 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-600 text-sm mb-4 md:mb-0">
            © {currentYear} ExpiTrack. All rights reserved.
          </p>
          <div className="flex items-center text-sm text-gray-500">
            <span>Made with</span>
            <Heart size={14} className="mx-1 text-red-500" />
            <span>by the ExpiTrack Team</span>
          </div>
        </div>
      </div>

      {showRecipeButton && <RecipeSuggestionsFloatingButton />}
    </footer>
  );
};

export default Footer;
